import React, { useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import CloseIcon from '@mui/icons-material/Close'
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined'
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined'
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import useDocumentPreview, {
  IMAGE_EXTENSIONS,
  extensionOf,
} from '../components/Documents/useDocumentPreview'

const WORD_EXTENSIONS = ['doc', 'docx']
const DEFAULT_ACCEPT = 'application/pdf,.doc,.docx,image/*'

// Drag and drop ignores the input's `accept`, so dropped files are checked
// against the same rules. A rule is an extension (".docx"), a MIME wildcard
// ("image/*") or a MIME type ("application/pdf"). Browsers leave `file.type`
// empty for some files, so a MIME type also matches its subtype as extension.
// A null/empty `accept` takes any file type.
const isAccepted = (file, accept) => {
  if (!accept) return true
  const extension = extensionOf(file.name)
  return accept
    .split(',')
    .map((rule) => rule.trim().toLowerCase())
    .filter(Boolean)
    .some((rule) => {
      if (rule.startsWith('.')) return extension === rule.slice(1)
      if (rule.endsWith('/*')) {
        return (
          file.type.startsWith(rule.slice(0, -1)) ||
          (rule === 'image/*' && IMAGE_EXTENSIONS.includes(extension))
        )
      }
      return file.type === rule || extension === rule.split('/')[1]
    })
}

const isSameFile = (a, b) =>
  a.name === b.name && a.size === b.size && a.lastModified === b.lastModified

const formatSize = (bytes) =>
  bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`

const fileKind = (extension) => {
  if (IMAGE_EXTENSIONS.includes(extension))
    return { label: 'Image', Icon: ImageOutlinedIcon }
  if (extension === 'pdf')
    return { label: 'PDF', Icon: PictureAsPdfOutlinedIcon }
  if (WORD_EXTENSIONS.includes(extension))
    return { label: 'Word document', Icon: DescriptionOutlinedIcon }
  return { label: 'File', Icon: InsertDriveFileOutlinedIcon }
}

const FileRow = ({ extension, name, detail, muted, actions }) => {
  const { Icon } = fileKind(extension)
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.25}
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1.5,
        pl: 1.25,
        pr: 0.5,
        py: 0.75,
        minHeight: 48,
      }}
    >
      <Icon fontSize="small" color="action" sx={{ opacity: muted ? 0.5 : 1 }} />
      <Box flex={1} minWidth={0} sx={{ opacity: muted ? 0.6 : 1 }}>
        <Typography
          variant="body2"
          fontWeight={600}
          noWrap
          title={name}
          sx={{ textDecoration: muted ? 'line-through' : 'none' }}
        >
          {name}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          {detail}
        </Typography>
      </Box>
      <Stack direction="row" alignItems="center" flexShrink={0}>
        {actions}
      </Stack>
    </Stack>
  )
}

// Attachments for a record form. Saved documents (S3 keys) can be previewed
// and marked for removal — nothing is deleted until the parent saves, so a
// removal can be undone. New files are held locally and uploaded by the
// parent on save (see helpers/uploadDocument.js). `single` holds one new
// file at a time — picking another replaces it, and a saved document is
// shown as "will be replaced". `maxSizeMB` rejects larger files up front.
const DocumentsField = ({
  existingDocuments = [],
  removedDocuments = [],
  onToggleRemove,
  files,
  onFilesChange,
  disabled,
  single = false,
  accept = DEFAULT_ACCEPT,
  caption = 'Invoices, job sheets or photos — PDF, Word or images',
  acceptLabel = 'PDF, Word or image files',
  pendingLabel = 'Uploads when you save',
  maxSizeMB,
  existingLabel = (key, index) => `Document ${index + 1}`,
  // Original file name for a saved key, so downloads keep it
  existingName,
}) => {
  const inputRef = useRef()
  const [dragging, setDragging] = useState(false)
  const [rejected, setRejected] = useState(false)
  const { openPreview, previewModal } = useDocumentPreview()

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList ?? [])
    const maxBytes = maxSizeMB ? maxSizeMB * 1024 * 1024 : Infinity
    const accepted = incoming.filter(
      (file) => isAccepted(file, accept) && file.size <= maxBytes,
    )
    setRejected(accepted.length !== incoming.length)
    if (single) {
      if (accepted.length) onFilesChange([accepted[0]])
      return
    }
    onFilesChange([
      ...files,
      ...accepted.filter((file) => !files.some((f) => isSameFile(f, file))),
    ])
  }

  const browse = () => inputRef.current?.click()

  return (
    <Stack spacing={1}>
      <Box
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label="Add documents"
        aria-disabled={disabled}
        onClick={browse}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            browse()
          }
        }}
        onDragOver={(e) => {
          e.preventDefault()
          setDragging(true)
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault()
          setDragging(false)
          addFiles(e.dataTransfer.files)
        }}
        sx={{
          border: '1.5px dashed',
          borderColor: dragging ? 'contrast.main' : 'divider',
          bgcolor: dragging ? 'action.hover' : 'transparent',
          borderRadius: 2,
          px: 2,
          py: 2.5,
          textAlign: 'center',
          cursor: 'pointer',
          outline: 'none',
          transition: 'border-color 0.15s, background-color 0.15s',
          '&:hover, &:focus-visible': {
            borderColor: 'contrast.main',
            bgcolor: 'action.hover',
          },
          // Children would otherwise fire dragleave as the pointer crosses them
          '& > *': { pointerEvents: 'none' },
          ...(disabled && { pointerEvents: 'none', opacity: 0.6 }),
        }}
      >
        <CloudUploadOutlinedIcon color="action" />
        <Typography variant="body2" fontWeight={600} color="text.primary">
          Drop {single ? 'a file' : 'files'} here or{' '}
          <Box component="span" color="contrast.main">
            browse
          </Box>
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {caption}
        </Typography>
      </Box>
      <input
        ref={inputRef}
        type="file"
        hidden
        multiple={!single}
        accept={accept || undefined}
        onChange={(e) => {
          addFiles(e.target.files)
          // Reset so the same file can be picked again after being removed
          e.target.value = ''
        }}
      />
      {rejected && (
        <Typography variant="caption" color="error">
          {single ? 'That file was skipped' : 'Some files were skipped'} — only{' '}
          {acceptLabel}
          {maxSizeMB ? ` up to ${maxSizeMB} MB` : ''} can be attached.
        </Typography>
      )}

      {existingDocuments.map((key, i) => {
        const extension = extensionOf(key)
        const removed = removedDocuments.includes(key)
        // In single mode a newly picked file takes this one's place on save
        const replaced = single && !removed && files.length > 0
        return (
          <FileRow
            key={key}
            extension={extension}
            name={existingLabel(key, i)}
            detail={
              removed
                ? 'Will be removed when you save'
                : replaced
                  ? 'Will be replaced when you save'
                  : `${fileKind(extension).label} · Saved`
            }
            muted={removed || replaced}
            actions={
              removed ? (
                <Button
                  size="small"
                  onClick={() => onToggleRemove(key)}
                  disabled={disabled}
                >
                  Undo
                </Button>
              ) : (
                <>
                  <Tooltip title="Preview">
                    <IconButton
                      size="small"
                      onClick={() =>
                        openPreview({ key, name: existingName?.(key) })
                      }
                      aria-label={`Preview document ${i + 1}`}
                    >
                      <VisibilityOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Remove">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => onToggleRemove(key)}
                        disabled={disabled}
                        aria-label={`Remove document ${i + 1}`}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </>
              )
            }
          />
        )
      })}

      {files.map((file) => (
        <FileRow
          key={`${file.name}-${file.size}-${file.lastModified}`}
          extension={extensionOf(file.name)}
          name={file.name}
          detail={`${formatSize(file.size)} · ${pendingLabel}`}
          actions={
            <Tooltip title="Remove">
              <span>
                <IconButton
                  size="small"
                  onClick={() =>
                    onFilesChange(files.filter((f) => !isSameFile(f, file)))
                  }
                  disabled={disabled}
                  aria-label={`Remove ${file.name}`}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          }
        />
      ))}

      {previewModal}
    </Stack>
  )
}

export default DocumentsField
